/**
 * cart-recommendations-refresh.js
 *
 * Keeps a cart-seeded <product-recommendations> section in sync with the
 * cart. Shopify's recommendations endpoint only accepts a single product_id,
 * so on the cart page we seed the widget from the FIRST line item at page
 * load (see `sections/product-recommendations.liquid`). But the cart updates
 * via AJAX section morphing (`component-cart-items.js`) WITHOUT re-rendering
 * the recommendations section — so without this script the recommendations
 * stay frozen on the original first item (or point at a product that was just
 * removed).
 *
 * Here we listen for the theme's bubbling `cart:update` event (add / remove /
 * quantity change) and:
 *   - hide the widget when the cart becomes empty, and
 *   - re-point it at the current first line item's product when that changes,
 *     forcing <product-recommendations> to re-fetch (it reloads on attribute
 *     mutation, but skips when data-recommendations-performed === 'true', so
 *     we clear that flag first).
 *
 * Loaded from the section's initial server-rendered HTML (outside the morph
 * target) so the document listener is bound once and survives morphs.
 */
(() => {
  if (window.__cartRecsRefreshBound) return;
  window.__cartRecsRefreshBound = true;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Capture the widget reference NOW, at page load. The recommendations morph
  // (which happens later, on scroll-into-view) re-renders the section from the
  // recommendations endpoint — a context with no cart, so it flips
  // data-cart-seeded back to "false". We therefore can't re-query by that
  // attribute after load; we hold the element reference instead. The node is
  // morphed in place, so this reference stays valid across morphs.
  let widget = document.querySelector('product-recommendations[data-cart-seeded="true"]');

  /** @returns {HTMLElement|null} the cart recommendations widget, if present. */
  const getWidget = () => {
    if (widget && widget.isConnected) return widget;
    // Fallback: the reference was lost — re-acquire by seeded flag, then tag.
    widget =
      document.querySelector('product-recommendations[data-cart-seeded="true"]') ||
      document.querySelector('product-recommendations');
    return widget;
  };

  /**
   * Point the widget at a product and (re)load, or hide it when the cart is empty.
   * @param {HTMLElement} widget
   * @param {string|number|undefined} productId
   * @param {number} itemCount
   */
  const applyProductId = (widget, productId, itemCount) => {
    if (!itemCount || itemCount < 1 || !productId) {
      // Nothing left in the cart to recommend from.
      widget.classList.add('hidden');
      return;
    }

    widget.classList.remove('hidden');

    // Already pointed at this product — the recommendations are still valid.
    if (String(widget.dataset.productId) === String(productId)) return;

    // <product-recommendations> refuses to reload while it thinks
    // recommendations were already performed; clear that, then change the
    // product id. Both attribute mutations are batched into one observer
    // callback, which triggers exactly one re-fetch for the new product.
    widget.dataset.recommendationsPerformed = 'false';
    widget.dataset.productId = String(productId);
  };

  /**
   * @param {{ items?: Array<{ product_id: number }>, item_count?: number } | null | undefined} cart
   */
  const refreshFromCart = (cart) => {
    const widget = getWidget();
    if (!widget) return;

    const items = cart && cart.items;
    if (Array.isArray(items)) {
      const first = items[0];
      const count = typeof cart.item_count === 'number' ? cart.item_count : items.length;
      applyProductId(widget, first && first.product_id, count);
      return;
    }

    // The event payload didn't include line items — ask the cart directly.
    fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => {
        if (!c) return;
        const first = c.items && c.items[0];
        applyProductId(widget, first && first.product_id, c.item_count);
      })
      .catch(() => {});
  };

  document.addEventListener('cart:update', (event) => {
    refreshFromCart(event && event.detail && event.detail.resource);
  });
})();
